import React, { useEffect, useRef, useState } from "react";
import api, { formatApiError } from "../lib/api";
import { toast } from "sonner";
import { useAppSettings, DEFAULT_SETTINGS, resolveImageUrl } from "../context/AppSettingsContext";
import LoyaltyCard from "../components/LoyaltyCard";
import DatePresetsEditor from "../components/DatePresetsEditor";
import BrandPreview from "../components/BrandPreview";
import BillingCard from "../components/BillingCard";
import CashbackSettingsCard from "../components/CashbackSettingsCard";
import ThermalPrinterCard from "../components/ThermalPrinterCard";
import ImageCropDialog from "../components/ImageCropDialog";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Palette, Upload, Save, RotateCcw, Printer, Image as ImageIcon, Gift, Trash2, Wallet, RefreshCw, BellRing } from "lucide-react";

const COLOR_FIELDS = [
  { key: "primary_color", label: "Colore primario console (bottoni/CTA)" },
  { key: "accent_color", label: "Colore accento (punti/QR)" },
  { key: "client_brand_color", label: "Colore brand Web App Cliente (accesso, registrazione, portale)" },
  { key: "card_bg", label: "Sfondo carta" },
  { key: "card_text", label: "Testo carta" },
];

// Choices available for the mobile bottom-nav slot dropdowns
const BOTNAV_CHOICES = [
  { to: "/", label: "Dashboard" },
  { to: "/banco", label: "Negozio (POS)" },
  { to: "/rilascio-card", label: "Rilascio Card" },
  { to: "/clienti", label: "Clienti" },
  { to: "/ricerca", label: "Ricerca avanzata" },
  { to: "/statistiche", label: "Statistiche" },
  { to: "/movimenti", label: "Movimenti" },
  { to: "/prenotazioni", label: "Prenotazioni" },
  { to: "/ordini-cliente", label: "Ordini Cliente" },
  { to: "/premi", label: "Catalogo Premi" },
  { to: "/prodotti", label: "Prodotti" },
  { to: "/campagne", label: "Campagne" },
  { to: "/coupon", label: "Coupon" },
  { to: "/compleanni", label: "Compleanni & Anniv." },
  { to: "/cluster", label: "Cluster Clienti" },
  { to: "/report", label: "Report PDF" },
  { to: "/autorizzazioni", label: "Autorizzazioni" },
  { to: "/messaggi", label: "Messaggi & News" },
  { to: "/volantini", label: "Volantini" },
  { to: "/comunicazioni", label: "Comunicazioni" },
  { to: "/sponsor", label: "Sponsor & Banner" },
];

const BOTNAV_PRESETS = [
  { key: "manager", label: "Manager", icon: "📊",
    slots: ["/", "/statistiche", "/campagne"] },
  { key: "pos", label: "Cassa (POS)", icon: "🛒",
    slots: ["/banco", "/ordini-cliente", "/clienti"] },
  { key: "marketing", label: "Marketing", icon: "📣",
    slots: ["/campagne", "/coupon", "/cluster"] },
];

const SAMPLE_CUSTOMER = {
  name: "Marco Rossi",
  card_number: "K0001",
  points: 240,
};

export default function AppSettings() {
  const { settings, setSettings, reload } = useAppSettings();
  const [form, setForm] = useState(settings);
  const [uploading, setUploading] = useState(false);
  const [uploadingPrivacy, setUploadingPrivacy] = useState(false);
  const privacyFileRef = useRef(null);
  const [saving, setSaving] = useState(false);
  const [coupons, setCoupons] = useState([]);
  const [welcomeCoupon, setWelcomeCoupon] = useState("none");
  const [milestone, setMilestone] = useState({ step: 500, bonus: 10, enabled: true, tiers: [] });
  const [milestoneSaving, setMilestoneSaving] = useState(false);
  const [branches, setBranches] = useState([]);
  useEffect(() => { api.get("/branches").then((r) => setBranches(r.data || [])).catch(() => {}); }, []);
  const fileRef = useRef(null);
  const cardRef = useRef(null);
  const cardBgRef = useRef(null);

  React.useEffect(() => { setForm(settings); }, [settings]);
  React.useEffect(() => {
    (async () => {
      try {
        const [{ data: cs }, { data: wc }, { data: ms }] = await Promise.all([
          api.get("/coupons"),
          api.get("/settings/welcome-coupon"),
          api.get("/settings/milestone"),
        ]);
        setCoupons(cs);
        setWelcomeCoupon(wc.coupon_id || "none");
        setMilestone({ step: ms.step, bonus: ms.bonus, enabled: !!ms.enabled, tiers: ms.tiers || [] });
      } catch {}
    })();
  }, []);

  const saveMilestone = async () => {
    setMilestoneSaving(true);
    try {
      const payload = { ...milestone };
      // strip empty tiers list to keep DB clean & pick correct mode
      if (!payload.tiers || payload.tiers.length === 0) {
        payload.tiers = [];
      } else {
        // basic client-side normalization; server also validates
        payload.tiers = payload.tiers
          .map((t) => ({ name: String(t.name || "").trim(), threshold: Number(t.threshold) || 0, bonus: Number(t.bonus) || 0 }))
          .filter((t) => t.name);
      }
      const { data } = await api.put("/settings/milestone", payload);
      setMilestone({ step: data.step, bonus: data.bonus, enabled: !!data.enabled, tiers: data.tiers || [] });
      toast.success("Bonus milestone salvato");
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setMilestoneSaving(false); }
  };

  const saveWelcomeCoupon = async (v) => {
    setWelcomeCoupon(v);
    try {
      await api.put("/settings/welcome-coupon", { coupon_id: v === "none" ? null : v });
      toast.success(v === "none" ? "Coupon benvenuto disattivato" : "Coupon benvenuto salvato");
    } catch (err) { toast.error(err?.message || "Errore"); }
  };

  const change = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const walletStripRef = useRef(null);
  const walletLogoRef = useRef(null);
  const uploadWalletLogo = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      fd.append("kind", "wallet");
      const { data } = await api.post("/uploads/image", fd, { headers: { "Content-Type": "multipart/form-data" } });
      change("wallet_logo_url", data.url);
      toast.success("Logo pass caricato");
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setUploading(false); e.target.value = ""; }
  };
  const [stripCrop, setStripCrop] = useState(null); // { file, target: "default" | seasonalIndex }
  const [walletOps, setWalletOps] = useState({ busy: "", stats: null });
  const loadWalletOps = () => api.get("/wallet/reminders/stats").then((r) => setWalletOps((w) => ({ ...w, stats: r.data }))).catch(() => {});
  useEffect(() => { loadWalletOps(); }, []);
  const pushAllPasses = async () => {
    setWalletOps((w) => ({ ...w, busy: "push" }));
    try {
      const { data } = await api.post("/wallet/push-all");
      toast.success(`Pass aggiornati: ${data.apple_devices || 0} dispositivi Apple (${data.apple_customers || 0} clienti), ${data.google_objects || 0} Google${data.google_class ? " · classe Google aggiornata" : ""}`);
      loadWalletOps();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setWalletOps((w) => ({ ...w, busy: "" })); }
  };
  const sendWalletReminders = async () => {
    setWalletOps((w) => ({ ...w, busy: "rem" }));
    try {
      const { data } = await api.post("/wallet/reminders/run-now");
      if (data.reason === "wallet_not_configured") toast.error("Wallet non configurato: nessun promemoria inviato");
      else toast.success(`Promemoria Wallet: ${data.customers || 0} clienti · ${data.pushed || 0} push · ${data.whatsapp || 0} WhatsApp`);
      loadWalletOps();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setWalletOps((w) => ({ ...w, busy: "" })); }
  };
  const uploadWalletStrip = (e, target = "default") => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (file) setStripCrop({ file, target });
  };
  const onStripCropped = async (blob) => {
    const c = stripCrop; setStripCrop(null);
    if (!c) return;
    try {
      const fd = new FormData();
      fd.append("file", blob || c.file, blob ? "striscia.jpg" : c.file.name);
      fd.append("kind", "wallet");
      const { data } = await api.post("/uploads/image", fd, { headers: { "Content-Type": "multipart/form-data" } });
      if (c.target === "default") change("wallet_strip_url", data.url);
      else setSeasonal(c.target, { image_url: data.url });
      toast.success("Striscia caricata: salva per applicarla ai pass");
    } catch (err) { toast.error(formatApiError(err)); }
  };
  const seasonal = form.wallet_seasonal_strips || [];
  const setSeasonal = (i, patch) => change("wallet_seasonal_strips", seasonal.map((s, k) => (k === i ? { ...s, ...patch } : s)));
  const addSeasonal = () => change("wallet_seasonal_strips", [...seasonal, { name: "", start_at: "", end_at: "", image_url: "", bg_color: "" }]);
  const removeSeasonal = (i) => change("wallet_seasonal_strips", seasonal.filter((_, k) => k !== i));
  const todayIso = new Date().toISOString().slice(0, 10);
  const activeSeasonal = seasonal.find((s) => s.image_url && s.start_at && s.end_at && s.start_at <= todayIso && todayIso <= s.end_at);

  const uploadPresetIcon = async (presetKey, e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/uploads/image", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const next = { ...(form.botnav_preset_icons || {}), [presetKey]: data.thumb_url || data.url };
      change("botnav_preset_icons", next);
      toast.success("Icona preset caricata");
    } catch (err) { toast.error(formatApiError(err)); }
    finally { e.target.value = ""; }
  };

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/settings/app", form);
      setSettings({ ...DEFAULT_SETTINGS, ...data });
      toast.success("Impostazioni salvate");
      reload();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setSaving(false); }
  };

  const resetDefaults = () => {
    setForm(DEFAULT_SETTINGS);
    toast.info("Ripristinati i valori di default (ricorda di salvare)");
  };

  const uploadLogo = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      fd.append("kind", "logo");
      const { data } = await api.post("/uploads/image", fd, { headers: { "Content-Type": "multipart/form-data" } });
      change("logo_url", data.url);
      toast.success("Logo caricato");
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setUploading(false); e.target.value = ""; }
  };

  const uploadPrivacyPdf = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setUploadingPrivacy(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const { data } = await api.post("/uploads/pdf", fd, { headers: { "Content-Type": "multipart/form-data" } });
      change("privacy_url", data.url);
      toast.success("PDF informativa caricato: ricordati di salvare");
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setUploadingPrivacy(false); e.target.value = ""; }
  };

  const [dailyPushBusy, setDailyPushBusy] = useState(false);
  const [dailyPushResult, setDailyPushResult] = useState("");
  const sendDailyPushNow = async () => {
    setDailyPushBusy(true);
    try {
      const { data } = await api.post("/segments/daily-product/run-now");
      setDailyPushResult(`Inviate ${data.sent || 0} notifiche a ${data.customers || 0} clienti`);
      toast.success("Push Prodotto del Giorno inviata");
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setDailyPushBusy(false); }
  };

  const uploadCardBg = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      fd.append("kind", "card_bg");
      const { data } = await api.post("/uploads/image", fd, { headers: { "Content-Type": "multipart/form-data" } });
      change("card_bg_image_url", data.url);
      toast.success("Sfondo carta caricato");
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setUploading(false); e.target.value = ""; }
  };

  const printCard = () => {
    downloadSample("print");
  };
  const downloadSample = async (layout) => {
    try {
      const res = await api.get("/card-codes/sample.pdf", { params: { layout, code: "0000000000000", fmt: "qrcode" }, responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a"); a.href = url; a.download = `card-${layout}-${(form.app_name || "club").replace(/\s+/g, "-")}.pdf`; document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
      toast.success(layout === "print" ? "PDF tipografia scaricato (abbondanza 3 mm + crocini)" : "PDF card scaricato");
    } catch (err) { toast.error(formatApiError(err)); }
  };
  const printCardHtml = () => {
    const url = resolveImageUrl(form.logo_url);
    const html = `<!DOCTYPE html><html><head><meta charset="utf-8"><title>${form.app_name} – Card</title>
      <style>
        body{margin:0;padding:32px;background:#f4f4f5;font-family:'Outfit',sans-serif;color:${form.card_text};}
        .wrap{max-width:520px;margin:0 auto;}
        .card{aspect-ratio:1.586/1;border-radius:20px;padding:28px;position:relative;overflow:hidden;
          background:linear-gradient(135deg, ${form.card_bg}, ${form.card_bg});
          color:${form.card_text}; box-shadow:0 20px 60px -20px ${form.primary_color}55;}
        .top{display:flex;justify-content:space-between;align-items:flex-start;gap:12px;}
        .brand{display:flex;align-items:center;gap:10px;}
        .logo{width:44px;height:44px;border-radius:10px;background:${form.primary_color};display:flex;align-items:center;justify-content:center;color:#fff;font-weight:800;}
        .name{font-weight:800;font-size:14px;letter-spacing:0.12em;text-transform:uppercase;}
        h1{font-size:24px;margin:24px 0 8px;}
        .num{font-family:'JetBrains Mono',monospace;letter-spacing:0.18em;font-weight:600;}
        .foot{display:flex;justify-content:space-between;align-items:flex-end;margin-top:24px;}
        .pt{color:${form.accent_color};font-weight:900;font-size:32px;}
        .tag{position:absolute;bottom:14px;left:28px;opacity:.6;font-size:11px;}
        @media print { body{background:#fff;} }
      </style></head><body>
      <div class="wrap">
        <div class="card">
          <div class="top">
            <div class="brand">${url ? `<img src="${url}" class="logo" style="object-fit:cover;background:#000;" />` : `<div class="logo">🔥</div>`}<div class="name">${form.app_name}</div></div>
            <img src="https://api.qrserver.com/v1/create-qr-code/?size=180x180&margin=6&bgcolor=ffffff&color=${encodeURIComponent(form.card_bg.replace('#',''))}&data=${encodeURIComponent(SAMPLE_CUSTOMER.card_number)}" width="90" height="90" style="border-radius:8px" />
          </div>
          <h1>{{NOME_CLIENTE}}</h1>
          <div class="foot">
            <div><div style="font-size:10px;letter-spacing:.15em;text-transform:uppercase;opacity:.7">N° Carta</div><div class="num">{{NUMERO_CARTA}}</div></div>
            <div style="text-align:right"><div style="font-size:10px;letter-spacing:.15em;text-transform:uppercase;opacity:.7">Punti</div><div class="pt">{{PUNTI}}</div></div>
          </div>
          ${form.card_tagline ? `<div class="tag">${form.card_tagline}</div>` : ""}
        </div>
        <p style="text-align:center;color:#71717a;font-size:12px;margin-top:16px;">${form.card_footer || ""}</p>
      </div>
      <script>window.print();</script>
      </body></html>`;
    const w = window.open("", "_blank");
    w.document.write(html);
    w.document.close();
  };

  const logoResolved = resolveImageUrl(form.logo_url);

  return (
    <div className="space-y-6 animate-fade-up" data-testid="settings-page">
      <div className="flex flex-col sm:flex-row justify-between gap-3">
        <div>
          <span className="pill pill-red"><Palette className="h-3 w-3" /> BRANDING</span>
          <h1 className="font-heading text-3xl sm:text-4xl font-extrabold mt-3">Personalizza l'app</h1>
          <p className="text-zinc-400 mt-1">Nome, logo, colori e grafica della carta fedeltà.</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={resetDefaults} className="border-zinc-800">
            <RotateCcw className="h-4 w-4 mr-1" /> Default
          </Button>
          <Button data-testid="settings-save" onClick={save} disabled={saving} className="bg-[color:var(--paprika)] hover:bg-[color:var(--paprika-hover)]">
            <Save className="h-4 w-4 mr-1" /> Salva
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
        {/* FORM */}
        <div className="space-y-4">
          <div className="card-elev p-5 space-y-4">
            <h3 className="font-heading font-semibold text-lg">Identità</h3>
            <div>
              <Label className="text-zinc-300 mb-1 inline-block">Nome applicazione</Label>
              <Input
                data-testid="settings-app-name"
                value={form.app_name}
                onChange={(e) => change("app_name", e.target.value)}
                className="bg-zinc-950 border-zinc-800"
              />
            </div>
            <div>
              <Label className="text-zinc-300 mb-1 inline-block flex items-center gap-1">
                <ImageIcon className="h-3 w-3" /> Logo
              </Label>
              <div className="flex items-center gap-3">
                <div className="h-16 w-16 rounded-xl bg-zinc-950 border border-zinc-800 flex items-center justify-center overflow-hidden">
                  {logoResolved ? (
                    <img src={logoResolved} alt="logo" className="w-full h-full object-cover" />
                  ) : (
                    <ImageIcon className="h-6 w-6 text-zinc-600" />
                  )}
                </div>
                <div className="flex-1 space-y-2">
                  <input ref={fileRef} type="file" accept="image/*" onChange={uploadLogo} className="hidden" data-testid="settings-logo-input" />
                  <Button
                    data-testid="settings-logo-upload"
                    variant="outline"
                    className="border-zinc-800 w-full"
                    onClick={() => fileRef.current?.click()}
                    disabled={uploading}
                  >
                    <Upload className="h-4 w-4 mr-2" />
                    {uploading ? "Caricamento…" : logoResolved ? "Sostituisci logo" : "Carica logo (jpg/png/webp)"}
                  </Button>
                  {logoResolved && (
                    <Button variant="ghost" size="sm" onClick={() => change("logo_url", null)} className="text-zinc-500">
                      Rimuovi logo
                    </Button>
                  )}
                </div>
              </div>
            </div>

            <div>
              <Label className="text-zinc-300 mb-2 flex items-center gap-1 text-xs uppercase tracking-widest">
                <ImageIcon className="h-3 w-3" /> Sfondo carta globale (fallback per sedi senza grafica dedicata)
              </Label>
              <div className="flex items-center gap-3">
                <div className="h-16 w-24 rounded-xl bg-zinc-950 border border-zinc-800 flex items-center justify-center overflow-hidden">
                  {form.card_bg_image_url ? (
                    <img
                      src={resolveImageUrl(form.card_bg_image_url)}
                      alt="card bg"
                      className="w-full h-full object-cover"
                      data-testid="settings-cardbg-preview"
                    />
                  ) : (
                    <div className="w-full h-full" style={{ background: form.card_bg }} />
                  )}
                </div>
                <div className="flex-1 space-y-2">
                  <input
                    ref={cardBgRef}
                    type="file"
                    accept="image/*"
                    onChange={uploadCardBg}
                    className="hidden"
                    data-testid="settings-cardbg-input"
                  />
                  <Button
                    data-testid="settings-cardbg-upload"
                    variant="outline"
                    className="border-amber-500/40 text-amber-300 hover:bg-amber-500/10 w-full"
                    onClick={() => cardBgRef.current?.click()}
                    disabled={uploading}
                  >
                    <Upload className="h-4 w-4 mr-2" />
                    {uploading ? "Caricamento…" : form.card_bg_image_url ? "Sostituisci sfondo" : "Carica PNG sfondo carta"}
                  </Button>
                  {form.card_bg_image_url && (
                    <Button
                      data-testid="settings-cardbg-remove"
                      variant="ghost"
                      size="sm"
                      onClick={() => change("card_bg_image_url", null)}
                      className="text-zinc-500"
                    >
                      Rimuovi sfondo
                    </Button>
                  )}
                </div>
              </div>
              <p className="text-[11px] text-zinc-500 mt-2">
                Consigliato: 1600×1010px, PNG con soggetto centrato. Verrà usato ovunque manchi la grafica sede.
              </p>
            </div>
          </div>

          <div className="card-elev p-5 space-y-4">
            <h3 className="font-heading font-semibold text-lg flex items-center gap-2">
              <Gift className="h-4 w-4 text-amber-300" /> Coupon Benvenuto
            </h3>
            <p className="text-xs text-zinc-500">
              Il coupon selezionato viene assegnato automaticamente ai nuovi iscritti dal portale online.
              Consigliato: uno sconto 10% o un valore fisso.
            </p>
            <div>
              <Label className="text-zinc-300 mb-1 inline-block">Coupon da regalare</Label>
              <select
                data-testid="welcome-coupon-select"
                value={welcomeCoupon}
                onChange={(e) => saveWelcomeCoupon(e.target.value)}
                className="w-full h-11 rounded-md px-3 bg-zinc-950 border border-zinc-800 text-sm"
              >
                <option value="none">Nessuno (disattivato)</option>
                {coupons.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name} · {c.code} · {c.discount_type === "percent" ? `${c.discount_value}%` : `€ ${c.discount_value}`}
                  </option>
                ))}
              </select>
            </div>
            {welcomeCoupon !== "none" && (
              <div className="text-[11px] text-emerald-300">✓ Ogni nuovo cliente registrato online riceverà questo coupon.</div>
            )}
          </div>

          {/* Bonus milestone (admin config) */}
          <div className="card-elev p-5 space-y-3" data-testid="milestone-panel">
            <div className="flex items-center justify-between gap-2">
              <div>
                <h3 className="font-heading font-semibold text-lg">🏆 Bonus milestone fedeltà</h3>
                <p className="text-xs text-zinc-500 mt-1">Bonus automatico ai clienti quando maturano punti. Puoi scegliere tra modalità <span className="text-zinc-300 font-medium">lineare</span> (ogni N punti) o <span className="text-zinc-300 font-medium">Tier</span> (Bronze/Silver/Gold).</p>
              </div>
              <label className="inline-flex items-center gap-2 text-xs cursor-pointer">
                <input
                  data-testid="milestone-enabled"
                  type="checkbox"
                  checked={!!milestone.enabled}
                  onChange={(e) => setMilestone({ ...milestone, enabled: e.target.checked })}
                  className="h-4 w-4 accent-emerald-500"
                />
                Attivo
              </label>
            </div>

            {/* Mode toggle: linear vs tiers */}
            <div className="flex gap-1 bg-zinc-950 border border-zinc-800 rounded-md p-1 w-fit" data-testid="milestone-mode-toggle">
              <button
                data-testid="milestone-mode-linear"
                onClick={() => setMilestone({ ...milestone, tiers: [] })}
                className={`px-3 py-1.5 rounded text-xs font-medium transition-colors ${(!milestone.tiers || milestone.tiers.length === 0) ? "bg-[color:var(--paprika)] text-white" : "text-zinc-400 hover:text-zinc-100"}`}
              >Lineare</button>
              <button
                data-testid="milestone-mode-tiers"
                onClick={() => {
                  if (!milestone.tiers || milestone.tiers.length === 0) {
                    setMilestone({ ...milestone, tiers: [
                      { name: "Bronze", threshold: 500, bonus: 15 },
                      { name: "Silver", threshold: 1500, bonus: 40 },
                      { name: "Gold", threshold: 3000, bonus: 100 },
                    ]});
                  }
                }}
                className={`px-3 py-1.5 rounded text-xs font-medium transition-colors ${(milestone.tiers && milestone.tiers.length > 0) ? "bg-[color:var(--paprika)] text-white" : "text-zinc-400 hover:text-zinc-100"}`}
              >Tier (Bronze/Silver/Gold)</button>
            </div>

            {(!milestone.tiers || milestone.tiers.length === 0) ? (
              <>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-zinc-300 mb-1 inline-block text-xs">Soglia (punti)</Label>
                    <Input
                      data-testid="milestone-step"
                      type="number"
                      min={0}
                      max={100000}
                      value={milestone.step}
                      onChange={(e) => setMilestone({ ...milestone, step: parseInt(e.target.value || "0", 10) })}
                      className="bg-zinc-950 border-zinc-800 font-mono"
                    />
                  </div>
                  <div>
                    <Label className="text-zinc-300 mb-1 inline-block text-xs">Bonus (punti)</Label>
                    <Input
                      data-testid="milestone-bonus"
                      type="number"
                      min={0}
                      max={100000}
                      value={milestone.bonus}
                      onChange={(e) => setMilestone({ ...milestone, bonus: parseInt(e.target.value || "0", 10) })}
                      className="bg-zinc-950 border-zinc-800 font-mono"
                    />
                  </div>
                </div>
                <div className="text-[11px] text-zinc-500">
                  Esempio: soglia <span className="font-mono text-amber-300">{milestone.step}</span> · bonus <span className="font-mono text-emerald-300">+{milestone.bonus}</span> → un cliente con 1.500 punti riceverà <span className="font-mono">{3 * (milestone.bonus || 0)}</span> punti bonus (3 milestone × {milestone.bonus}).
                </div>
              </>
            ) : (
              <div className="space-y-2" data-testid="milestone-tiers-list">
                {milestone.tiers.map((tier, idx) => (
                  <div key={idx} className="grid grid-cols-12 gap-2 items-end p-2 bg-zinc-950/60 rounded-lg border border-zinc-800" data-testid={`milestone-tier-${idx}`}>
                    <div className="col-span-4">
                      <Label className="text-zinc-500 text-[10px] uppercase tracking-widest">Nome</Label>
                      <Input
                        data-testid={`milestone-tier-name-${idx}`}
                        value={tier.name}
                        maxLength={32}
                        onChange={(e) => {
                          const t = [...milestone.tiers]; t[idx] = { ...t[idx], name: e.target.value };
                          setMilestone({ ...milestone, tiers: t });
                        }}
                        className="bg-zinc-950 border-zinc-800 h-9"
                      />
                    </div>
                    <div className="col-span-3">
                      <Label className="text-zinc-500 text-[10px] uppercase tracking-widest">Soglia</Label>
                      <Input
                        data-testid={`milestone-tier-threshold-${idx}`}
                        type="number"
                        min={0}
                        value={tier.threshold}
                        onChange={(e) => {
                          const t = [...milestone.tiers]; t[idx] = { ...t[idx], threshold: parseInt(e.target.value || "0", 10) };
                          setMilestone({ ...milestone, tiers: t });
                        }}
                        className="bg-zinc-950 border-zinc-800 h-9 font-mono"
                      />
                    </div>
                    <div className="col-span-3">
                      <Label className="text-zinc-500 text-[10px] uppercase tracking-widest">Bonus</Label>
                      <Input
                        data-testid={`milestone-tier-bonus-${idx}`}
                        type="number"
                        min={0}
                        value={tier.bonus}
                        onChange={(e) => {
                          const t = [...milestone.tiers]; t[idx] = { ...t[idx], bonus: parseInt(e.target.value || "0", 10) };
                          setMilestone({ ...milestone, tiers: t });
                        }}
                        className="bg-zinc-950 border-zinc-800 h-9 font-mono"
                      />
                    </div>
                    <div className="col-span-2 flex justify-end">
                      <Button
                        data-testid={`milestone-tier-delete-${idx}`}
                        size="sm"
                        variant="outline"
                        onClick={() => {
                          const t = milestone.tiers.filter((_, i) => i !== idx);
                          setMilestone({ ...milestone, tiers: t });
                        }}
                        className="h-9 border-red-500/30 text-red-300 hover:bg-red-500/10"
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                  </div>
                ))}
                <Button
                  data-testid="milestone-tier-add"
                  size="sm"
                  variant="outline"
                  onClick={() => {
                    const t = [...(milestone.tiers || []), { name: `Tier ${(milestone.tiers?.length || 0) + 1}`, threshold: 0, bonus: 0 }];
                    setMilestone({ ...milestone, tiers: t });
                  }}
                  className="border-zinc-800 w-full"
                >
                  + Aggiungi tier
                </Button>
                <div className="text-[11px] text-zinc-500">
                  Ogni tier viene assegnato <span className="text-zinc-300">una sola volta</span> quando il cliente supera la soglia. Ordinati automaticamente per soglia crescente al salvataggio.
                </div>
              </div>
            )}

            <Button
              data-testid="milestone-save"
              onClick={saveMilestone}
              disabled={milestoneSaving}
              size="sm"
              className="bg-[color:var(--paprika)] hover:bg-[color:var(--paprika-hover)]"
            >
              <Save className="h-3.5 w-3.5 mr-1" /> {milestoneSaving ? "Salvataggio..." : "Salva bonus milestone"}
            </Button>
          </div>

          <div className="card-elev p-5 space-y-4">
            <h3 className="font-heading font-semibold text-lg">Colori</h3>
            <div className="grid grid-cols-2 gap-3">
              {COLOR_FIELDS.map((f) => (
                <div key={f.key}>
                  <Label className="text-zinc-300 mb-1 inline-block text-xs">{f.label}</Label>
                  <div className="flex gap-2 items-center">
                    <input
                      data-testid={`settings-color-${f.key}`}
                      type="color"
                      value={form[f.key]}
                      onChange={(e) => change(f.key, e.target.value)}
                      className="h-10 w-14 rounded-md bg-transparent border border-zinc-800 cursor-pointer"
                    />
                    <Input
                      value={form[f.key]}
                      onChange={(e) => change(f.key, e.target.value)}
                      className="bg-zinc-950 border-zinc-800 font-mono text-sm"
                    />
                  </div>
                </div>
              ))}
            </div>
            <BrandPreview color={form.client_brand_color} appName={form.app_name} />
            <div className="grid sm:grid-cols-2 gap-3 pt-2 border-t border-zinc-800" data-testid="settings-theme-extra">
              {[["client_bg_color", "Sfondo Home / portale"], ["client_text_color", "Colore testo"]].map(([k, l]) => (
                <div key={k}><Label className="text-zinc-500 text-[11px] mb-1 inline-block">{l}</Label><div className="flex items-center gap-2"><input type="color" value={form[k] || (k === "client_bg_color" ? "#FFFFFF" : "#18181B")} onChange={(e) => change(k, e.target.value.toUpperCase())} className="h-9 w-10 rounded border border-zinc-800 bg-transparent" data-testid={`settings-${k}`} /><Input value={form[k] || ""} onChange={(e) => change(k, e.target.value)} className="bg-zinc-950 border-zinc-800 font-mono text-xs" /></div></div>
              ))}
              <div><Label className="text-zinc-500 text-[11px] mb-1 inline-block">Carattere</Label><select value={form.client_font || "default"} onChange={(e) => change("client_font", e.target.value)} className="w-full h-10 rounded-md bg-zinc-950 border border-zinc-800 px-3 text-sm" data-testid="settings-client-font"><option value="default">Standard</option><option value="rounded">Arrotondato</option><option value="serif">Elegante</option><option value="modern">Moderno</option></select></div>
              <div><Label className="text-zinc-500 text-[11px] mb-1 inline-block">Stile card e pulsanti</Label><select value={form.client_card_style || "soft"} onChange={(e) => change("client_card_style", e.target.value)} className="w-full h-10 rounded-md bg-zinc-950 border border-zinc-800 px-3 text-sm" data-testid="settings-client-card-style"><option value="soft">Morbido</option><option value="sharp">Squadrato</option><option value="pill">Pillola</option></select></div>
            </div>
          </div>

          <div className="card-elev p-5 space-y-4">
            <h3 className="font-heading font-semibold text-lg">Testi carta</h3>
            <div>
              <Label className="text-zinc-300 mb-1 inline-block">Slogan sulla carta</Label>
              <Input
                data-testid="settings-tagline"
                value={form.card_tagline || ""}
                onChange={(e) => change("card_tagline", e.target.value)}
                className="bg-zinc-950 border-zinc-800"
              />
            </div>
            <div>
              <Label className="text-zinc-300 mb-1 inline-block">Note in fondo (stampa)</Label>
              <Textarea
                data-testid="settings-footer"
                value={form.card_footer || ""}
                onChange={(e) => change("card_footer", e.target.value)}
                className="bg-zinc-950 border-zinc-800"
                rows={2}
              />
            </div>

            {/* Grafica pass Apple / Google Wallet */}
            <div className="border-t border-zinc-800 pt-4" data-testid="settings-wallet-section">
              <Label className="text-zinc-300 mb-2 flex items-center gap-1 text-xs uppercase tracking-widest">
                <Wallet className="h-3.5 w-3.5" /> Grafica pass Wallet (Apple / Google)
              </Label>
              <p className="text-[11px] text-zinc-500 mb-2">Il pass usa il logo del club e questi colori. L'immagine "striscia" compare sotto il logo nel pass Apple e come immagine principale su Google Wallet (consigliato 1125×432 px, jpg/png). Le modifiche arrivano ai pass già salvati al prossimo aggiornamento punti.</p>
              <div className="grid grid-cols-3 gap-2">
                {[["wallet_bg_color", "Sfondo", form.client_brand_color || "#1E40AF"], ["wallet_text_color", "Testo", "#FFFFFF"], ["wallet_label_color", "Etichette", "#E6ECFF"]].map(([k, l, def]) => (
                  <div key={k}><Label className="text-zinc-500 text-[11px] mb-1 inline-block">{l}</Label>
                    <div className="flex items-center gap-1.5">
                      <input type="color" value={form[k] || def} onChange={(e) => change(k, e.target.value.toUpperCase())} className="h-9 w-10 rounded border border-zinc-800 bg-transparent" data-testid={`settings-${k.replace(/_/g, "-")}`} />
                      <Input value={form[k] || ""} onChange={(e) => change(k, e.target.value)} placeholder={def} className="bg-zinc-950 border-zinc-800 font-mono text-xs" />
                    </div>
                  </div>
                ))}
              </div>
              <div className="mt-2 flex items-center gap-3">
                {form.wallet_strip_url ? <img src={resolveImageUrl(form.wallet_strip_url)} alt="" className="h-12 w-32 object-cover rounded-md border border-zinc-800" data-testid="settings-wallet-strip-preview" /> : <div className="h-12 w-32 rounded-md bg-zinc-900 border border-zinc-800 flex items-center justify-center text-[10px] text-zinc-500">nessuna striscia</div>}
                <input ref={walletStripRef} type="file" accept="image/*" className="hidden" onChange={(e) => uploadWalletStrip(e, "default")} data-testid="settings-wallet-strip-file" />
                <Button type="button" variant="outline" size="sm" className="border-zinc-700" onClick={() => walletStripRef.current?.click()} data-testid="settings-wallet-strip-upload"><Upload className="h-3.5 w-3.5 mr-1" /> Carica e ritaglia</Button>
                {form.wallet_strip_url && <Button type="button" variant="ghost" size="sm" onClick={() => change("wallet_strip_url", "")} data-testid="settings-wallet-strip-remove"><Trash2 className="h-3.5 w-3.5" /></Button>}
              </div>
              <ImageCropDialog file={stripCrop?.file} open={!!stripCrop} onClose={() => setStripCrop(null)} onDone={onStripCropped} defaultRatio="pass" outWidth={1125} />
              <div className="mt-2 flex items-center gap-3" data-testid="settings-wallet-logo-row">
                <div className="h-12 w-12 rounded-md border border-zinc-800 flex items-center justify-center overflow-hidden" style={{ background: form.wallet_bg_color || form.client_brand_color || "#1E40AF" }}>
                  {(form.wallet_logo_url || form.logo_url) ? <img src={resolveImageUrl(form.wallet_logo_url || form.logo_url)} alt="" className="max-h-10 max-w-10 object-contain" data-testid="settings-wallet-logo-preview" /> : <span className="text-[10px] text-white/70">logo</span>}
                </div>
                <div className="text-[11px] text-zinc-500 flex-1">Logo del pass (versione chiara per lo sfondo colorato). Se vuoto usa il logo del club.</div>
                <input ref={walletLogoRef} type="file" accept="image/*" className="hidden" onChange={uploadWalletLogo} data-testid="settings-wallet-logo-file" />
                <Button type="button" variant="outline" size="sm" className="border-zinc-700" onClick={() => walletLogoRef.current?.click()} data-testid="settings-wallet-logo-upload"><Upload className="h-3.5 w-3.5 mr-1" /> Logo pass</Button>
                {form.wallet_logo_url && <Button type="button" variant="ghost" size="sm" onClick={() => change("wallet_logo_url", "")} data-testid="settings-wallet-logo-remove"><Trash2 className="h-3.5 w-3.5" /></Button>}
              </div>
              <div className="mt-3 rounded-xl border border-zinc-800 p-3 space-y-2" data-testid="settings-wallet-seasonal">
                <div className="flex items-center justify-between">
                  <Label className="text-zinc-300 text-xs">Strisce stagionali (programmate per data)</Label>
                  <Button type="button" variant="outline" size="sm" className="border-zinc-700" onClick={addSeasonal} data-testid="settings-wallet-seasonal-add">+ Aggiungi periodo</Button>
                </div>
                <p className="text-[11px] text-zinc-500">Nel periodo indicato il pass usa questa striscia (e, se impostato, questo sfondo) al posto di quella standard. Es. Natale, estate, evento.</p>
                {seasonal.length === 0 && <div className="text-xs text-zinc-500" data-testid="settings-wallet-seasonal-empty">Nessun periodo programmato.</div>}
                {seasonal.map((s, i) => (
                  <div key={i} className={`rounded-lg border p-2 grid grid-cols-2 sm:grid-cols-[1fr_1fr_1fr_auto] gap-2 items-end ${activeSeasonal === s ? "border-emerald-500/60 bg-emerald-500/5" : "border-zinc-800"}`} data-testid={`settings-wallet-seasonal-${i}`}>
                    <div className="col-span-2 sm:col-span-1"><Label className="text-zinc-500 text-[11px]">Nome</Label><Input value={s.name || ""} onChange={(e) => setSeasonal(i, { name: e.target.value })} placeholder="Natale 2026" className="bg-zinc-950 border-zinc-800" data-testid={`settings-wallet-seasonal-name-${i}`} /></div>
                    <div><Label className="text-zinc-500 text-[11px]">Dal</Label><Input type="date" value={s.start_at || ""} onChange={(e) => setSeasonal(i, { start_at: e.target.value })} className="bg-zinc-950 border-zinc-800" data-testid={`settings-wallet-seasonal-start-${i}`} /></div>
                    <div><Label className="text-zinc-500 text-[11px]">Al (incluso)</Label><Input type="date" value={s.end_at || ""} onChange={(e) => setSeasonal(i, { end_at: e.target.value })} className="bg-zinc-950 border-zinc-800" data-testid={`settings-wallet-seasonal-end-${i}`} /></div>
                    <div className="col-span-2 sm:col-span-1 flex items-center gap-2">
                      {s.image_url ? <img src={resolveImageUrl(s.image_url)} alt="" className="h-9 w-24 object-cover rounded border border-zinc-800" data-testid={`settings-wallet-seasonal-preview-${i}`} /> : <div className="h-9 w-24 rounded bg-zinc-900 border border-zinc-800 text-[10px] text-zinc-500 flex items-center justify-center">striscia</div>}
                      <label className="inline-flex items-center gap-1 px-2 py-1.5 rounded-md border border-zinc-700 text-xs cursor-pointer hover:bg-zinc-900"><Upload className="h-3 w-3" /><input type="file" accept="image/*" className="hidden" onChange={(e) => uploadWalletStrip(e, i)} data-testid={`settings-wallet-seasonal-file-${i}`} /></label>
                      <input type="color" value={s.bg_color || form.wallet_bg_color || form.client_brand_color || "#1E40AF"} onChange={(e) => setSeasonal(i, { bg_color: e.target.value.toUpperCase() })} title="Sfondo nel periodo" className="h-8 w-8 rounded border border-zinc-800 bg-transparent" data-testid={`settings-wallet-seasonal-bg-${i}`} />
                      <button type="button" onClick={() => removeSeasonal(i)} className="text-zinc-500 hover:text-red-400" data-testid={`settings-wallet-seasonal-remove-${i}`}><Trash2 className="h-4 w-4" /></button>
                    </div>
                    {activeSeasonal === s && <div className="col-span-full text-[11px] text-emerald-400" data-testid={`settings-wallet-seasonal-active-${i}`}>● Attiva oggi: i pass mostrano questa striscia</div>}
                  </div>
                ))}
              </div>
              <div className="mt-3 rounded-2xl overflow-hidden border border-zinc-800 max-w-xs" style={{ background: activeSeasonal?.bg_color || form.wallet_bg_color || form.client_brand_color || "#1E40AF", color: form.wallet_text_color || "#FFFFFF" }} data-testid="settings-wallet-preview">
                <div className="flex items-center justify-between px-4 pt-3">
                  <div className="flex items-center gap-2">{form.logo_url && <img src={resolveImageUrl(form.logo_url)} alt="" className="h-6 w-6 object-contain rounded bg-white p-0.5" />}<span className="text-xs font-semibold">{form.app_name}</span></div>
                  <div className="text-right"><div className="text-[9px] uppercase" style={{ color: form.wallet_label_color || "#E6ECFF" }}>Punti</div><div className="text-base font-bold leading-none">145</div></div>
                </div>
                {(activeSeasonal?.image_url || form.wallet_strip_url) ? <img src={resolveImageUrl(activeSeasonal?.image_url || form.wallet_strip_url)} alt="" className="w-full h-20 object-cover mt-2" /> : <div className="h-20 mt-2 opacity-20" style={{ background: "repeating-linear-gradient(45deg, #fff 0 8px, transparent 8px 16px)" }} />}
                <div className="px-4 py-3 flex justify-between text-[10px]"><div><div className="uppercase" style={{ color: form.wallet_label_color || "#E6ECFF" }}>Titolare</div><div className="text-xs font-semibold">Mario Rossi</div></div><div className="text-right"><div className="uppercase" style={{ color: form.wallet_label_color || "#E6ECFF" }}>Carta</div><div className="text-xs font-mono">W254875</div></div></div>
              </div>
              <div className="mt-3 rounded-xl border border-zinc-800 p-3 space-y-2" data-testid="settings-wallet-ops">
                <div className="flex flex-wrap items-center gap-2">
                  <Button type="button" size="sm" className="bg-zinc-800" disabled={walletOps.busy === "push"} onClick={pushAllPasses} data-testid="settings-wallet-push-all">
                    <RefreshCw className={`h-3.5 w-3.5 mr-1 ${walletOps.busy === "push" ? "animate-spin" : ""}`} /> Aggiorna tutti i pass
                  </Button>
                  <Button type="button" variant="outline" size="sm" className="border-zinc-700" disabled={walletOps.busy === "rem"} onClick={sendWalletReminders} data-testid="settings-wallet-reminders-run">
                    <BellRing className="h-3.5 w-3.5 mr-1" /> Invia promemoria Wallet ora
                  </Button>
                </div>
                <p className="text-[11px] text-zinc-500">"Aggiorna tutti i pass" spinge subito la grafica salvata ai Wallet dei clienti (Apple via notifica push, Google aggiornando la classe) senza aspettare un movimento punti. Salva prima le impostazioni. Il promemoria Wallet parte da solo ogni mattina (08:00) per chi ha la card da almeno 3 giorni e non l'ha salvata: push + messaggio nel portale + WhatsApp (automazione Spoki <code>wallet_reminder</code> da collegare in /spoki).</p>
                {walletOps.stats && (
                  <div className="text-[11px] text-zinc-400 flex flex-wrap gap-x-4 gap-y-1" data-testid="settings-wallet-ops-stats">
                    <span>In attesa di promemoria: <b className="text-zinc-200" data-testid="settings-wallet-reminders-pending">{walletOps.stats.pending}</b></span>
                    <span>Promemoria inviati: <b className="text-zinc-200">{walletOps.stats.reminded}</b> (WhatsApp {walletOps.stats.whatsapp})</span>
                    <span>Hanno salvato dopo il promemoria: <b className="text-emerald-400">{walletOps.stats.converted}</b></span>
                    {walletOps.stats.push_all?.at && <span data-testid="settings-wallet-push-all-last">Ultimo "Aggiorna tutti": {new Date(walletOps.stats.push_all.at).toLocaleString("it-IT")} · Apple {walletOps.stats.push_all.apple_devices} · Google {walletOps.stats.push_all.google_objects}</span>}
                  </div>
                )}
              </div>
            </div>

            {/* Concorso attivo (iframe embed lato cliente + admin) */}
            <div className="border-t border-zinc-800 pt-4">
              <Label className="text-zinc-300 mb-2 flex items-center gap-1 text-xs uppercase tracking-widest">
                🎮 Concorso attivo
              </Label>
              <div className="space-y-2">
                <div>
                  <Label className="text-zinc-500 text-[11px] mb-1 inline-block">Titolo (mostrato sopra il gioco)</Label>
                  <Input
                    data-testid="settings-contest-title"
                    value={form.contest_title || ""}
                    onChange={(e) => change("contest_title", e.target.value)}
                    placeholder="Concorsi"
                    className="bg-zinc-950 border-zinc-800"
                  />
                </div>
                <div>
                  <Label className="text-zinc-500 text-[11px] mb-1 inline-block">URL del gioco (iframe embed)</Label>
                  <Input
                    data-testid="settings-contest-url"
                    value={form.contest_url || ""}
                    onChange={(e) => { const m = e.target.value.match(/src=["']([^"']+)["']/); change("contest_url", m ? m[1] : e.target.value); }}
                    placeholder="https://nouvellecontest.it/embed/... (puoi incollare anche l'intero <iframe>)"
                    className="bg-zinc-950 border-zinc-800 font-mono text-[12px]"
                  />
                  <p className="text-[10px] text-zinc-500 mt-1">
                    Lascia vuoto per nascondere il tab Concorsi lato cliente. L'URL deve permettere l'embed via iframe (senza X-Frame-Options SAMEORIGIN).
                  </p>
                </div>
                <div>
                  <Label className="text-zinc-500 text-[11px] mb-1 inline-block">Punti bonus per partecipazione (0 = disattivo)</Label>
                  <Input
                    data-testid="settings-contest-bonus"
                    type="number" min="0" max="1000"
                    value={form.contest_bonus_points ?? 0}
                    onChange={(e) => change("contest_bonus_points", Math.max(0, parseInt(e.target.value || "0", 10)))}
                    className="bg-zinc-950 border-zinc-800 w-40"
                  />
                  <p className="text-[10px] text-zinc-500 mt-1">Il cliente riceve i punti premendo "Ho partecipato" nel portale, al massimo una volta al giorno.</p>
                </div>
                <div>
                  <Label className="text-zinc-500 text-[11px] mb-1 inline-block">Premio classifica settimanale (testo mostrato ai clienti)</Label>
                  <Input
                    data-testid="settings-contest-prize"
                    value={form.contest_weekly_prize || ""}
                    onChange={(e) => change("contest_weekly_prize", e.target.value)}
                    placeholder="es. Buono spesa da 20 € al primo classificato"
                    className="bg-zinc-950 border-zinc-800"
                  />
                  <p className="text-[10px] text-zinc-500 mt-1">La classifica (lun–dom) conta le partecipazioni completate; lascia vuoto per non mostrare il premio.</p>
                </div>
                <div>
                  <Label className="text-zinc-500 text-[11px] mb-1 inline-block">Coupon assegnato automaticamente al vincitore (ogni lunedì)</Label>
                  <select
                    data-testid="settings-contest-coupon"
                    value={form.contest_weekly_coupon_id || ""}
                    onChange={(e) => change("contest_weekly_coupon_id", e.target.value || null)}
                    className="w-full h-10 rounded-md bg-zinc-950 border border-zinc-800 px-3 text-sm"
                  >
                    <option value="">Nessun coupon (solo avviso)</option>
                    {(coupons || []).map((c) => <option key={c.id} value={c.id}>{c.name} · {c.code}</option>)}
                  </select>
                  <p className="text-[10px] text-zinc-500 mt-1">Il lunedì alle 08:00 il 1° classificato riceve il coupon, una push e un WhatsApp; la classifica viene archiviata nello storico.</p>
                </div>
              </div>
            </div>

            <div className="border-t border-zinc-800 pt-4 flex items-start justify-between gap-4" data-testid="settings-card-groups-panel">
              <div>
                <Label className="text-zinc-300 mb-1 flex items-center gap-1 text-xs uppercase tracking-widest">👨‍👩‍👧 Gruppi carta (Famiglia / CRAL / Azienda)</Label>
                <p className="text-[11px] text-zinc-500">Se attivo, dalla scheda cliente puoi creare gruppi di carte con codici diversi che condividono il saldo punti: ogni membro accumula nel gruppo e può riscattare dal saldo comune.</p>
              </div>
              <input type="checkbox" checked={!!form.card_groups_enabled} onChange={(e) => change("card_groups_enabled", e.target.checked)} className="h-5 w-5 mt-1 shrink-0" data-testid="settings-card-groups-enabled" />
            </div>

            {/* Prodotto del giorno */}
            <div className="border-t border-zinc-800 pt-4" data-testid="settings-daily-product-panel">
              <div className="flex items-start justify-between gap-4 mb-3">
                <div>
                  <Label className="text-zinc-300 mb-1 flex items-center gap-1 text-xs uppercase tracking-widest">✨ Prodotto del giorno (portale cliente)</Label>
                  <p className="text-[11px] text-zinc-500">Ogni giorno viene messo in evidenza un prodotto diverso (scelto automaticamente tra quelli attivi con foto) nel tile "Prodotti" e nel menù pubblico della sede, con pulsante "Ordina ora".</p>
                </div>
                <input type="checkbox" checked={form.daily_product_enabled !== false} onChange={(e) => change("daily_product_enabled", e.target.checked)} className="h-5 w-5 mt-1 shrink-0" data-testid="settings-daily-product-enabled" />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <div>
                  <Label className="text-zinc-500 text-[11px] mb-1 inline-block">Sconto del giorno (%)</Label>
                  <Input
                    type="number" min={0} max={90}
                    data-testid="settings-daily-product-discount"
                    value={form.daily_product_discount_pct ?? 0}
                    onChange={(e) => change("daily_product_discount_pct", Math.max(0, Math.min(90, parseInt(e.target.value) || 0)))}
                    className="bg-zinc-950 border-zinc-800"
                  />
                  <p className="text-[10px] text-zinc-600 mt-1">0 = solo "consigliato", senza sconto</p>
                </div>
                <div className="sm:col-span-2">
                  <Label className="text-zinc-500 text-[11px] mb-1 inline-block">Etichetta</Label>
                  <Input
                    data-testid="settings-daily-product-label"
                    value={form.daily_product_label || ""}
                    onChange={(e) => change("daily_product_label", e.target.value)}
                    placeholder="Prodotto del giorno"
                    className="bg-zinc-950 border-zinc-800"
                  />
                </div>
              </div>
              <div className="mt-3 flex items-center gap-3 flex-wrap">
                <Button type="button" variant="outline" size="sm" disabled={dailyPushBusy} onClick={sendDailyPushNow} data-testid="settings-daily-product-push-now" className="border-zinc-800">
                  {dailyPushBusy ? "Invio…" : "🔔 Invia ora la push ai clienti"}
                </Button>
                <span className="text-[11px] text-zinc-500">Automatica ogni mattina alle 08:00 (Roma) ai clienti con notifiche attive.</span>
                {dailyPushResult && <span className="text-[11px] text-emerald-400" data-testid="settings-daily-product-push-result">{dailyPushResult}</span>}
              </div>
            </div>


            {/* Informativa privacy per i form di registrazione */}
            <div className="border-t border-zinc-800 pt-4" data-testid="settings-privacy-panel">
              <Label className="text-zinc-300 mb-2 flex items-center gap-1 text-xs uppercase tracking-widest">
                🔒 Informativa privacy (form Registrazione e Rilascio Card)
              </Label>
              <p className="text-[11px] text-zinc-500 mb-3">
                Il link compare accanto ai consensi GDPR. Incolla l'indirizzo della tua pagina privacy oppure carica il PDF dell'informativa.
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <div className="sm:col-span-2">
                  <Label className="text-zinc-500 text-[11px] mb-1 inline-block">Link pagina privacy o PDF</Label>
                  <div className="flex gap-2">
                    <Input
                      data-testid="settings-privacy-url"
                      value={form.privacy_url || ""}
                      onChange={(e) => change("privacy_url", e.target.value)}
                      placeholder="https://www.tuosito.it/privacy"
                      className="bg-zinc-950 border-zinc-800 font-mono text-[12px]"
                    />
                    <input ref={privacyFileRef} type="file" accept="application/pdf" onChange={uploadPrivacyPdf} className="hidden" data-testid="settings-privacy-pdf-input" />
                    <Button type="button" variant="outline" disabled={uploadingPrivacy} onClick={() => privacyFileRef.current?.click()} data-testid="settings-privacy-pdf-upload" className="border-zinc-800 h-10 shrink-0">
                      {uploadingPrivacy ? "Carico…" : "Carica PDF"}
                    </Button>
                  </div>
                  {form.privacy_url && (
                    <a href={resolveImageUrl(form.privacy_url)} target="_blank" rel="noreferrer" className="text-[11px] text-sky-300 underline mt-1 inline-block" data-testid="settings-privacy-preview">Apri anteprima</a>
                  )}
                </div>
                <div>
                  <Label className="text-zinc-500 text-[11px] mb-1 inline-block">Testo del link</Label>
                  <Input
                    data-testid="settings-privacy-label"
                    value={form.privacy_label || ""}
                    onChange={(e) => change("privacy_label", e.target.value)}
                    placeholder="Leggi l'informativa privacy"
                    className="bg-zinc-950 border-zinc-800"
                  />
                </div>
              </div>
            </div>

            {/* Bottom nav mobile personalizzabile */}
            <div className="border-t border-zinc-800 pt-4" data-testid="settings-bottom-nav-panel">
              <Label className="text-zinc-300 mb-2 flex items-center gap-1 text-xs uppercase tracking-widest">
                📱 Bottom nav mobile — 3 scorciatoie
              </Label>
              <p className="text-[11px] text-zinc-500 mb-3">
                Riordina con drag & drop. La quarta è sempre "Prodotti".
              </p>

              {/* Preset predefiniti (con icona custom uploadable) */}
              <div className="flex flex-wrap gap-1.5 mb-3 items-start" data-testid="botnav-presets">
                {BOTNAV_PRESETS.map((p) => {
                  const iconUrl = (form.botnav_preset_icons || {})[p.key];
                  const resolved = iconUrl ? resolveImageUrl(iconUrl) : null;
                  return (
                    <div key={p.key} className="flex flex-col items-center gap-1">
                      <button
                        type="button"
                        data-testid={`botnav-preset-${p.key}`}
                        onClick={() => change("mobile_bottom_nav_slots", p.slots)}
                        className="text-[10px] px-2.5 py-1 rounded-full border border-zinc-800 text-zinc-300 hover:border-amber-500/60 hover:text-amber-300 transition flex items-center gap-1"
                        title={p.slots.map((s) => (BOTNAV_CHOICES.find((c) => c.to === s)?.label || s)).join(" · ")}
                      >
                        {resolved ? (
                          <img src={resolved} alt={p.label} className="h-4 w-4 rounded object-cover" />
                        ) : (
                          <span>{p.icon}</span>
                        )}
                        {p.label}
                      </button>
                      <label className="text-[9px] text-zinc-500 hover:text-amber-300 cursor-pointer" data-testid={`botnav-preset-icon-${p.key}`}>
                        {resolved ? "🖼️ Cambia" : "📎 Icona"}
                        <input
                          type="file"
                          accept="image/*"
                          className="hidden"
                          onChange={(e) => uploadPresetIcon(p.key, e)}
                        />
                      </label>
                      {resolved && (
                        <button
                          type="button"
                          onClick={() => {
                            const next = { ...(form.botnav_preset_icons || {}), [p.key]: null };
                            change("botnav_preset_icons", next);
                          }}
                          data-testid={`botnav-preset-icon-remove-${p.key}`}
                          className="text-[9px] text-red-400 hover:text-red-300"
                        >
                          ✕ Rimuovi
                        </button>
                      )}
                    </div>
                  );
                })}
              </div>

              {/* Slot list (drag & drop) */}
              <BotNavSlots
                slots={form.mobile_bottom_nav_slots || DEFAULT_SETTINGS.mobile_bottom_nav_slots}
                onChange={(next) => change("mobile_bottom_nav_slots", next)}
              />

              <div className="text-[10px] text-emerald-300 mt-2">
                Suggerito: le 3 pagine che apri più spesso dallo smartphone.
              </div>
            </div>

            <DatePresetsEditor
              presets={form.date_presets || []}
              onChange={(next) => change("date_presets", next)}
            />

            <div className="border-t border-zinc-800 pt-4" data-testid="settings-default-branch-panel">
              <Label className="text-zinc-300 mb-2 flex items-center gap-1 text-xs uppercase tracking-widest">🏬 Sede predefinita movimenti</Label>
              <p className="text-[11px] text-zinc-500 mb-2">
                Usata quando l'operatore non ha una sede e il cliente non ne ha una preferita (evita movimenti "Senza sede" nelle statistiche). Lo staff resta sempre vincolato alla propria sede.
              </p>
              <select
                data-testid="settings-default-branch"
                value={form.default_branch_id || ""}
                onChange={(e) => change("default_branch_id", e.target.value || null)}
                className="h-9 w-full sm:w-80 rounded-md bg-zinc-950 border border-zinc-800 text-sm text-zinc-200 px-2"
              >
                <option value="">— Prima sede creata —</option>
                {branches.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
              </select>
            </div>
          </div>
        </div>

        {/* PREVIEW */}
        <div className="space-y-4">
          <BillingCard />
          <CashbackSettingsCard />
          <ThermalPrinterCard />
          <div className="card-elev p-5 space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="font-heading font-semibold text-lg">Anteprima carta fedeltà</h3>
              <div className="flex gap-2">
                <Button data-testid="settings-print-html" size="sm" variant="ghost" className="text-zinc-400" onClick={printCardHtml} title="Stampa rapida dal browser">
                  <Printer className="h-3.5 w-3.5" />
                </Button>
                <Button data-testid="settings-print" size="sm" variant="outline" className="border-zinc-800" onClick={printCard} title="PDF per la tipografia: 85,6×54 mm, abbondanza 3 mm, crocini, QR e numero carta">
                  <Printer className="h-3.5 w-3.5 mr-1" /> PDF tipografia
                </Button>
              </div>
            </div>
            <p className="text-xs text-zinc-500">L'anteprima si aggiorna in tempo reale.</p>
            <AppSettingsLivePreview form={form}>
              <LoyaltyCard customer={SAMPLE_CUSTOMER} />
            </AppSettingsLivePreview>
          </div>
        </div>
      </div>
    </div>
  );
}

// Applica le impostazioni al provider al volo per l'anteprima
function AppSettingsLivePreview({ form, children }) {
  const { setSettings, settings } = useAppSettings();
  const prev = React.useRef(settings);
  React.useEffect(() => {
    setSettings({ ...settings, ...form });
    return () => { setSettings(prev.current); };
    // eslint-disable-next-line
  }, [JSON.stringify(form)]);
  return children;
}

// Drag & drop reorder + inline change picker for the 3 bottom-nav slots
function BotNavSlots({ slots, onChange }) {
  const [dragIdx, setDragIdx] = React.useState(null);
  const list = [0, 1, 2].map((i) => slots[i] || BOTNAV_CHOICES[0].to);

  const onDragStart = (idx) => setDragIdx(idx);
  const onDragOver = (e) => e.preventDefault();
  const onDrop = (targetIdx) => {
    if (dragIdx === null || dragIdx === targetIdx) return;
    const next = [...list];
    const [moved] = next.splice(dragIdx, 1);
    next.splice(targetIdx, 0, moved);
    onChange(next);
    setDragIdx(null);
  };
  const swap = (idx, dir) => {
    const j = idx + dir;
    if (j < 0 || j > 2) return;
    const next = [...list];
    [next[idx], next[j]] = [next[j], next[idx]];
    onChange(next);
  };

  return (
    <div className="space-y-1.5" data-testid="botnav-slots-list">
      {list.map((path, idx) => {
        const label = BOTNAV_CHOICES.find((c) => c.to === path)?.label || path;
        const isDrag = dragIdx === idx;
        return (
          <div
            key={idx}
            draggable
            onDragStart={() => onDragStart(idx)}
            onDragOver={onDragOver}
            onDrop={() => onDrop(idx)}
            onDragEnd={() => setDragIdx(null)}
            data-testid={`botnav-slot-row-${idx}`}
            className={`flex items-center gap-2 p-2 rounded-md border transition ${isDrag ? "border-amber-500 bg-amber-500/10" : "border-zinc-800 bg-zinc-950 hover:border-zinc-600"}`}
          >
            <span className="text-zinc-500 cursor-grab select-none" title="Trascina per riordinare">⋮⋮</span>
            <span className="text-[10px] font-mono text-zinc-500 w-6">#{idx + 1}</span>
            <select
              data-testid={`settings-botnav-slot-${idx}`}
              value={path}
              onChange={(e) => {
                const next = [...list];
                next[idx] = e.target.value;
                onChange(next);
              }}
              className="flex-1 h-8 rounded px-2 bg-zinc-900 border border-zinc-800 text-xs"
            >
              {BOTNAV_CHOICES.map((c) => (
                <option key={c.to} value={c.to}>{c.label}</option>
              ))}
            </select>
            <div className="flex gap-0.5">
              <button
                type="button"
                data-testid={`botnav-slot-up-${idx}`}
                onClick={() => swap(idx, -1)}
                disabled={idx === 0}
                className="text-zinc-500 hover:text-white disabled:opacity-30 w-6 h-6 rounded text-xs"
                title="Su"
              >▲</button>
              <button
                type="button"
                data-testid={`botnav-slot-down-${idx}`}
                onClick={() => swap(idx, 1)}
                disabled={idx === 2}
                className="text-zinc-500 hover:text-white disabled:opacity-30 w-6 h-6 rounded text-xs"
                title="Giù"
              >▼</button>
            </div>
            <span className="text-[10px] text-zinc-600 hidden sm:inline truncate max-w-[80px]">{label}</span>
          </div>
        );
      })}
    </div>
  );
}
